/**
 * KioskDemo — autonomous self-running demo for trade show / expo booth.
 *
 * Activation: add ?kiosk=1 to any URL. The component auto-logs in as the
 * kiosk account and runs 6 loops (A–F) continuously. A single click/tap
 * anywhere pauses kiosk and shows the interactive demo prompt.
 *
 * Loops:
 *   A — Cards & Collection   (browse, filter, sort, card detail)
 *   B — Gallery & Photos     (browse, flip, search, detail)
 *   C — Analytics & Value    (charts, brand/year/player clickthrough)
 *   D — Other Inventory      (wax boxes, packs, binders)
 *   E — AI & Smart Features  (Cy chat, Smart Fill, labels)
 *   F — Data & Admin         (CSV import, settings, value dictionary)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/api";

const KIOSK_USER     = "kiosk";
const KIOSK_PASSWORD = "D3m0CardStoard!";

// Card IDs in the kiosk account (local dev values — overridden after login via API)
const CARD_IDS = {
  berra54:   25481,
  berra52t:  25482,
  maris58:   25483,
  mantle59:  25484,
  mantle61:  25487,
  mantle58:  25488,
};

const WAX_ID    = 807;
const BINDER_ID = 409;

// Scripted Cy Q&A for kiosk (no API calls needed)
const CY_SCRIPT = [
  {
    q: "What is my most valuable card?",
    a: "Your most valuable card is the 1954 Bowman Yogi Berra #161 graded NM-MT+, valued at $1,125. It's a key vintage Bowman issue in excellent condition.",
  },
  {
    q: "Show me all my Topps rookie cards",
    a: "You have 23 Topps rookie cards in your collection. Top picks include the 1963 Topps Pete Rose RC, the 1968 Topps Nolan Ryan RC, and the 1968 Topps Johnny Bench RC — all strong vintage rookies.",
  },
  {
    q: "What is the total value of my 1950s cards?",
    a: "Your 1950s cards total approximately $8,450 across 31 cards. The Bowman and Topps issues from 1952–1959 make up the core, with Yogi Berra and Mickey Mantle cards accounting for nearly half that value.",
  },
];

// ── Loop definitions ──────────────────────────────────────────────────────────

function buildLoops(cardIds) {
  const { berra54, berra52t, maris58, mantle59, mantle61, mantle58 } = cardIds;

  return [
    // ── Loop A: Cards & Collection ────────────────────────────────────────
    [
      { type: "navigate",      path: "/list-cards" },
      { type: "wait",          ms: 3000 },

      // Browse the collection
      { type: "caption",       text: "📇 Browsing your full card collection..." },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 1800 },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 1800 },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 1800 },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 1800 },
      { type: "table-scroll",  px: -1600, ms: 80 }, { type: "wait", ms: 1200 },

      // Filter by Berra
      { type: "spotlight",     selector: "[data-tour='col-last']", ms: 300 },
      { type: "caption",       text: '🔍 Opening filter — typing "Berra"...' },
      { type: "type-filter",   col: "last", field: "last_name", value: "Berra" },
      { type: "wait",          ms: 1500 },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 2000 },
      { type: "table-scroll",  px: -400, ms: 80 },
      { type: "clear-filter",  col: "last", field: "last_name" },
      { type: "spotlight-off" },
      { type: "wait",          ms: 800 },

      // Filter by Jeter
      { type: "spotlight",     selector: "[data-tour='col-last']", ms: 300 },
      { type: "caption",       text: '🔍 Filtering for "Jeter"...' },
      { type: "type-filter",   col: "last", field: "last_name", value: "Jeter" },
      { type: "wait",          ms: 2500 },
      { type: "table-scroll",  px: 300, ms: 80 }, { type: "wait", ms: 1500 },
      { type: "table-scroll",  px: -300, ms: 80 },
      { type: "clear-filter",  col: "last", field: "last_name" },
      { type: "spotlight-off" },
      { type: "wait",          ms: 800 },

      // Sort dialog
      { type: "spotlight",     selector: "[data-tour='sort-btn']", ms: 300 },
      { type: "caption",       text: "⇅ Sort button — build a multi-level sort up to 9 levels..." },
      { type: "wait",          ms: 2000 },
      // Pre-load a multi-level sort config so the modal opens showing configured fields
      { type: "sort",          config: [
          { key: "card_value", direction: "desc" },
          { key: "last_name",  direction: "asc"  },
          { key: "year",       direction: "desc"  },
        ] },
      { type: "open-sort-modal" },
      { type: "spotlight",     selector: "[data-tour='sort-modal']", ms: 400 },
      { type: "caption",       text: "⇅ 3-level sort: Card Value ↓, Player ↑, Year ↓ — save as your default." },
      { type: "wait",          ms: 6000 },
      { type: "close-sort-modal" },
      { type: "spotlight-off" },
      { type: "wait",          ms: 1000 },

      // Browse sorted results
      { type: "caption",       text: "📇 Sorted by highest value first..." },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 2000 },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 2000 },
      { type: "table-scroll",  px: 400, ms: 80 }, { type: "wait", ms: 2000 },
      { type: "table-scroll",  px: -1200, ms: 80 }, { type: "wait", ms: 1000 },

      // Action buttons walkthrough
      { type: "focus-first-row" },
      { type: "spotlight",     selector: "[data-tour='kiosk-btn-pin']",    ms: 300 },
      { type: "caption",       text: "📌 Pin — lock a row to jump back to it anytime" },
      { type: "wait",          ms: 3000 },

      // Edit row demo
      { type: "spotlight",     selector: "[data-tour='kiosk-btn-edit']",   ms: 200 },
      { type: "caption",       text: "✏️ Edit — opens inline edit row in the grid..." },
      { type: "wait",          ms: 2000 },
      { type: "edit-first-row" },
      { type: "spotlight-off" },
      { type: "caption",       text: "✏️ Edit row open — update any field directly in the table" },
      { type: "wait",          ms: 4500 },
      { type: "cancel-edit" },
      { type: "wait",          ms: 1000 },

      // Copy row demo
      { type: "focus-first-row" },
      { type: "spotlight",     selector: "[data-tour='kiosk-btn-copy']",   ms: 200 },
      { type: "caption",       text: "📋 Copy — opens a duplicate as a new editable row..." },
      { type: "wait",          ms: 2000 },
      { type: "show-new-row" },
      { type: "spotlight-off" },
      { type: "caption",       text: "📋 New row ready — edit and save, or cancel to discard" },
      { type: "wait",          ms: 4000 },
      { type: "close-new-row" },
      { type: "wait",          ms: 800 },

      // Print label from list
      { type: "focus-first-row" },
      { type: "spotlight",     selector: "[data-tour='kiosk-btn-label']",  ms: 200 },
      { type: "caption",       text: "🖨️ Print Label — generates a scannable QR code label..." },
      { type: "wait",          ms: 2000 },
      { type: "print-first-label" },
      { type: "spotlight-off" },
      { type: "caption",       text: "🖨️ Label preview — scan the QR code to view card info" },
      { type: "wait",          ms: 4000 },
      { type: "close-modal" },
      { type: "wait",          ms: 800 },

      // Info + delete
      { type: "focus-first-row" },
      { type: "spotlight",     selector: "[data-tour='kiosk-btn-detail']", ms: 200 },
      { type: "caption",       text: "ℹ️ Card Detail — full record with images and value history" },
      { type: "wait",          ms: 3000 },
      { type: "spotlight",     selector: "[data-tour='kiosk-btn-delete']", ms: 200 },
      { type: "caption",       text: "✕ Delete — remove a card from the collection" },
      { type: "wait",          ms: 3000 },
      { type: "spotlight-off" },
      { type: "caption",       text: "Opening card detail — 1959 Topps Mickey Mantle" },
      { type: "navigate",      path: `/card-detail/${mantle59}` },
      { type: "wait",          ms: 2500 },
      { type: "spotlight",     selector: "[data-tour='detail-label-id']", ms: 200 },
      { type: "caption",       text: "🏷️ Label ID — unique QR identifier for every card" },
      { type: "wait",          ms: 3000 },
      { type: "spotlight",     selector: "[data-tour='detail-grade']", ms: 200 },
      { type: "caption",       text: "📊 Grade — condition rating that drives card value" },
      { type: "wait",          ms: 3000 },
      { type: "spotlight-off" },
      { type: "caption",       text: "📖 Book Values — 5-tier reference prices (Hi / HiMid / Mid / LoMid / Lo)" },
      { type: "wait",          ms: 3500 },
      { type: "spotlight",     selector: "[data-tour='detail-chips']", ms: 200 },
      { type: "caption",       text: "💵 Price Paid & Purchase Date — acquisition cost tracking" },
      { type: "wait",          ms: 3000 },
      { type: "spotlight",     selector: "[data-tour='detail-images']", ms: 200 },
      { type: "scroll",        px: 300, ms: 80 },
      { type: "caption",       text: "🖼️ Card Images — front and back photos in full resolution" },
      { type: "wait",          ms: 3500 },
      { type: "spotlight",     selector: "[data-tour='detail-actions']", ms: 200 },
      { type: "scroll",        px: 300, ms: 80 },
      { type: "caption",       text: "🖨️ Print Label — generates a scannable QR code label" },
      { type: "wait",          ms: 2500 },
      { type: "spotlight-off" },
      { type: "print-label" },
      { type: "wait",          ms: 4000 },
      { type: "close-modal" },
      { type: "scroll",        px: -600, ms: 80 },
      { type: "wait",          ms: 1000 },
      { type: "caption",       text: "1954 Bowman Yogi Berra..." },
      { type: "navigate",      path: `/card-detail/${berra54}` },
      { type: "wait",          ms: 4000 },
      { type: "caption",       text: "1958 Topps Roger Maris..." },
      { type: "navigate",      path: `/card-detail/${maris58}` },
      { type: "wait",          ms: 4000 },
    ],

    // ── Loop B: Gallery & Photos ──────────────────────────────────────────
    [
      { type: "navigate",      path: "/gallery" },
      { type: "caption",       text: "🎴 Gallery — browsing card photos..." },
      { type: "wait",          ms: 3000 },

      // 8 cards right — each shown front then flipped to back
      { type: "caption",       text: "🔄 Browsing — each card shown front and back..." },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },
      { type: "gallery-next" }, { type: "wait", ms: 1100 }, { type: "gallery-flip" }, { type: "wait", ms: 1100 },

      // Zoom in on current card
      { type: "caption",       text: "🔍+ Zooming in for a closer look..." },
      { type: "gallery-zoom-in",  ms: 300 },
      { type: "gallery-zoom-in",  ms: 300 },
      { type: "gallery-zoom-in",  ms: 300 },
      { type: "wait",          ms: 3000 },

      // Zoom back out
      { type: "caption",       text: "🔍− Zooming back out..." },
      { type: "gallery-zoom-out", ms: 300 },
      { type: "gallery-zoom-out", ms: 300 },
      { type: "gallery-zoom-out", ms: 300 },
      { type: "wait",          ms: 800 },

      // 7 cards back left — no flip
      { type: "caption",       text: "◀ Navigating back through the collection..." },
      { type: "gallery-prev" }, { type: "wait", ms: 1200 },
      { type: "gallery-prev" }, { type: "wait", ms: 1200 },
      { type: "gallery-prev" }, { type: "wait", ms: 1200 },
      { type: "gallery-prev" }, { type: "wait", ms: 1200 },
      { type: "gallery-prev" }, { type: "wait", ms: 1200 },
      { type: "gallery-prev" }, { type: "wait", ms: 1200 },
      { type: "gallery-prev" }, { type: "wait", ms: 1200 },

      // Search
      { type: "caption",       text: '🔍 Searching gallery for "Mantle"...' },
      { type: "gallery-search", value: "Mantle" },
      { type: "wait",          ms: 2500 },
      { type: "gallery-next",  count: 3, interval: 1800 },
      { type: "gallery-search", value: "" },
      { type: "wait",          ms: 1000 },

      // Open card detail
      { type: "caption",       text: "📋 Opening full card detail view..." },
      { type: "navigate",      path: `/card-detail/${mantle61}` },
      { type: "wait",          ms: 5000 },
      { type: "navigate",      path: `/card-detail/${berra52t}` },
      { type: "wait",          ms: 4000 },
    ],

    // ── Loop C: Analytics & Value ─────────────────────────────────────────
    [
      { type: "navigate", path: "/analytics" },
      { type: "caption",  text: "📊 Analytics — collection value at a glance..." },
      { type: "wait",     ms: 3000 },
      { type: "caption",  text: "📦 Inventory trend — cards added over time" },
      { type: "wait",     ms: 2500 },
      { type: "scroll",   px: 400, ms: 80 },
      { type: "wait",     ms: 2000 },
      { type: "caption",  text: "🏷️ Clicking Topps to filter card list..." },
      { type: "analytics-click", table: "brand", value: "Topps" },
      { type: "wait",     ms: 1500 },
      { type: "navigate", path: "/list-cards" },
      { type: "caption",  text: "📇 Cards filtered by Topps..." },
      { type: "wait",     ms: 3500 },
      { type: "navigate", path: "/analytics" },
      { type: "wait",     ms: 2000 },
      { type: "caption",  text: "📅 Clicking 1952 to filter by year..." },
      { type: "analytics-click", table: "year", value: "1952" },
      { type: "wait",     ms: 1500 },
      { type: "navigate", path: "/list-cards" },
      { type: "caption",  text: "📇 Cards filtered by year 1952..." },
      { type: "wait",     ms: 3500 },
      { type: "navigate", path: "/analytics" },
      { type: "wait",     ms: 2000 },
      { type: "caption",  text: "👤 Clicking Berra to filter by player..." },
      { type: "analytics-click", table: "player", value: "Berra" },
      { type: "wait",     ms: 1500 },
      { type: "navigate", path: "/list-cards" },
      { type: "caption",  text: "📇 Cards filtered by Yogi Berra..." },
      { type: "wait",     ms: 3500 },
    ],

    // ── Loop D: Other Inventory ───────────────────────────────────────────
    [
      { type: "navigate", path: "/wax" },
      { type: "caption",  text: "📦 Wax Boxes — vintage sealed inventory..." },
      { type: "scroll",   px: 400, ms: 80 },
      { type: "wait",     ms: 3000 },
      { type: "scroll",   px: -400, ms: 80 },
      { type: "wait",     ms: 1000 },
      { type: "navigate", path: "/packs" },
      { type: "caption",  text: "🧧 Wax Packs — individual pack inventory..." },
      { type: "scroll",   px: 400, ms: 80 },
      { type: "wait",     ms: 3000 },
      { type: "scroll",   px: -400, ms: 80 },
      { type: "wait",     ms: 1000 },
      { type: "navigate", path: "/set-binders" },
      { type: "caption",  text: "📓 Sets & Binders — organized set inventory..." },
      { type: "scroll",   px: 400, ms: 80 },
      { type: "wait",     ms: 3000 },
      { type: "scroll",   px: -400, ms: 80 },
      { type: "wait",     ms: 1000 },
      { type: "navigate", path: "/analytics" },
      { type: "caption",  text: "📊 Analytics — all inventory types tracked together..." },
      { type: "scroll",   px: 600, ms: 80 },
      { type: "wait",     ms: 3000 },
      { type: "scroll",   px: -600, ms: 80 },
      { type: "wait",     ms: 2000 },
    ],

    // ── Loop E: AI & Smart Features ───────────────────────────────────────
    [
      { type: "navigate", path: "/" },
      { type: "caption",  text: "💬 Cy — your AI Collection Assistant..." },
      { type: "open-chat" },
      { type: "wait",     ms: 2000 },
      { type: "chat-qa",  index: 0 },
      { type: "wait",     ms: 6000 },
      { type: "chat-qa",  index: 1 },
      { type: "wait",     ms: 6000 },
      { type: "chat-qa",  index: 2 },
      { type: "wait",     ms: 6000 },
      { type: "close-chat" },

      // Add Card + Smart Fill live demo
      { type: "navigate",       path: "/list-cards" },
      { type: "wait",           ms: 2000 },
      { type: "focus-first-row" },
      { type: "spotlight",      selector: "[data-tour='add-card-btn']", ms: 300 },
      { type: "caption",        text: "＋ Add Card — click to open an inline entry row..." },
      { type: "wait",           ms: 3000 },
      { type: "spotlight-off" },
      { type: "caption",        text: "✨ Smart Fill — type a player name and watch fields auto-populate..." },
      { type: "wait",           ms: 2000 },
      { type: "smart-fill-demo", first: "Nolan", last: "Ryan", year: "1968", brand: "Topps", wait: 5000 },
      { type: "caption",        text: "✨ Card number, year, brand, and rookie flag — all filled automatically!" },
      { type: "wait",           ms: 3500 },
      { type: "close-new-row" },
      { type: "wait",           ms: 1000 },

      // Try another player
      { type: "smart-fill-demo", first: "Mickey", last: "Mantle", year: "1959", brand: "Topps", wait: 5000 },
      { type: "caption",        text: "✨ Smart Fill works across 28,000+ cards in the dictionary" },
      { type: "wait",           ms: 3500 },
      { type: "close-new-row" },
      { type: "wait",           ms: 1000 },

      { type: "navigate",       path: "/import-cards" },
      { type: "caption",        text: "📥 CSV Import — bring in your entire collection at once..." },
      { type: "wait",           ms: 4000 },
    ],

    // ── Loop F: Admin Settings ────────────────────────────────────────────
    [
      { type: "navigate",       path: "/admin" },
      { type: "wait",           ms: 1500 },

      // Features section
      { type: "admin-expand",   id: "s-features" },
      { type: "spotlight",      selector: "[data-tour='admin-features']", ms: 300 },
      { type: "caption",        text: "🔧 Features — toggle Smart Fill, AI Chat, Image AI, Dark Mode..." },
      { type: "wait",           ms: 4000 },
      { type: "admin-collapse", id: "s-features" },
      { type: "spotlight-off" },
      { type: "wait",           ms: 500 },

      // General — Cards Per Page
      { type: "admin-expand",   id: "s-general" },
      { type: "spotlight",      selector: "[data-tour='admin-cards-per-page']", ms: 300 },
      { type: "caption",        text: "📄 Cards Per Page — control how many cards load at once (100 / 250 / 500 / 1000 / All)" },
      { type: "wait",           ms: 4000 },
      { type: "spotlight-off" },

      // Row Colors
      { type: "admin-expand",   id: "s-rowcolors" },
      { type: "spotlight",      selector: "[data-tour='admin-row-colors']", ms: 300 },
      { type: "caption",        text: "🎨 Row Colors — customize highlight colors for rookies, Mint cards, and Rookie+Mint combos" },
      { type: "wait",           ms: 4000 },
      { type: "admin-collapse", id: "s-general" },
      { type: "admin-collapse", id: "s-rowcolors" },
      { type: "spotlight-off" },
      { type: "wait",           ms: 500 },

      // Nav Bar
      { type: "admin-expand",   id: "s-navbar" },
      { type: "scroll",         px: 150, ms: 80 },
      { type: "caption",        text: "🔀 Nav Bar — show or hide individual nav buttons per user" },
      { type: "wait",           ms: 3500 },
      { type: "admin-collapse", id: "s-navbar" },
      { type: "scroll",         px: -150, ms: 80 },
      { type: "wait",           ms: 500 },

      // Valuation tab
      { type: "admin-tab",      tab: "valuation" },
      { type: "wait",           ms: 1000 },
      { type: "spotlight",      selector: "[data-tour='admin-valuation-apply']", ms: 300 },
      { type: "caption",        text: "💰 Apply Global Valuation — recalculate every card's value using current factors" },
      { type: "wait",           ms: 4000 },
      { type: "spotlight-off" },

      // Users tab
      { type: "admin-tab",      tab: "users" },
      { type: "wait",           ms: 1000 },
      { type: "spotlight",      selector: "[data-tour='admin-users']", ms: 300 },
      { type: "caption",        text: "👥 Users — assign subscription tiers and manage feature access per user" },
      { type: "wait",           ms: 4500 },
      { type: "spotlight-off" },

      // Data tab
      { type: "admin-tab",      tab: "data" },
      { type: "wait",           ms: 1000 },
      { type: "caption",        text: "📥 Data — import cards via CSV, backup and restore your full collection" },
      { type: "wait",           ms: 4000 },

      { type: "navigate",       path: "/" },
      { type: "caption",        text: "🏠 CardStoard — For collectors. By collectors." },
      { type: "wait",           ms: 3000 },
    ],
  ];
}

// ── Caption overlay ───────────────────────────────────────────────────────────

function KioskCaption({ text }) {
  if (!text) return null;
  return (
    <div style={{
      position: "fixed", bottom: 48, left: "50%", transform: "translateX(-50%)",
      background: "rgba(0,0,0,0.82)", color: "#fff",
      padding: "0.9rem 2.5rem", borderRadius: 40,
      fontSize: "1.35rem", fontWeight: 700, zIndex: 8000,
      pointerEvents: "none", whiteSpace: "nowrap",
      border: "1px solid rgba(255,255,255,0.2)",
      boxShadow: "0 6px 28px rgba(0,0,0,0.5)",
      letterSpacing: "0.01em",
    }}>
      {text}
    </div>
  );
}

// ── Pause overlay ─────────────────────────────────────────────────────────────

const LOOP_LABELS = [
  { key: "A", label: "A — Cards & Collection" },
  { key: "B", label: "B — Gallery & Photos" },
  { key: "C", label: "C — Analytics & Value" },
  { key: "D", label: "D — Other Inventory" },
  { key: "E", label: "E — AI & Smart Features" },
  { key: "F", label: "F — Admin Settings" },
];

function KioskPauseOverlay({ onResume, onJump }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9500,
      background: "rgba(0,0,0,0.82)", display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1.5rem",
    }}>
      <img src="/logo.png" alt="CardStoard" style={{ width: 400, maxWidth: "80vw" }} />
      <p style={{ color: "#fff", fontSize: "1.2rem", margin: 0 }}>Demo paused</p>

      {/* Loop selector */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
        <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }}>Jump to loop:</p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
          {LOOP_LABELS.map((l, i) => (
            <button key={l.key} className="nav-btn"
              style={{ fontSize: "0.85rem", padding: "0.35rem 0.9rem", background: "#334155" }}
              onClick={() => onJump(i)}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: "1rem" }}>
        <button className="nav-btn" style={{ fontSize: "1rem", padding: "0.6rem 1.8rem" }} onClick={onResume}>
          Resume Demo
        </button>
        <button className="nav-btn" style={{ fontSize: "1rem", padding: "0.6rem 1.8rem", background: "#1a7a1a" }}
          onClick={() => window.location.href = "/login"}>
          Interactive Demo →
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function KioskDemo() {
  const [active, setActive]     = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const { setUser, setIsLoggedIn, applyTheme, isLoggedIn: authLoggedIn } = useAuth();
  const [paused, setPaused]     = useState(false);
  const [caption, setCaption]   = useState("");
  const [loopIdx, setLoopIdx]   = useState(0);
  const [cardIds, setCardIds]   = useState(CARD_IDS);
  const [spotlight, setSpotlight] = useState(null);
  const navigate                = useNavigate();
  const location                = useLocation();
  const timerRef                = useRef(null);
  const stepRef                 = useRef(0);
  const runningRef              = useRef(false);
  const chatInputRef            = useRef(null);
  const loopIdxRef              = useRef(0);
  loopIdxRef.current = loopIdx; // kept current every render — avoids stale closure in run-loop effect

  // Detect ?kiosk=1 on home page (set by KioskEntry after login)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("kiosk") === "1" && authLoggedIn === true) {
      const loopMap = { A:0, B:1, C:2, D:3, E:4, F:5 };
      const loopKey = params.get("loop");
      if (loopKey && loopMap[loopKey] !== undefined) setLoopIdx(loopMap[loopKey]);
      setActive(true);
    }
  }, [authLoggedIn, location.search]); // eslint-disable-line

  // Once active and logged in, resolve card IDs then start loops
  useEffect(() => {
    if (!active || loggedIn || authLoggedIn !== true) return;
    (async () => {
      try {
        const res = await api.get("/cards/", { params: { limit: 200, sort_by: "card_value:desc" } });
        const cards = res.data || [];
        const find = (fn) => cards.find(fn)?.id;
        setCardIds({
          berra54:  find(c => c.last_name === "Berra"  && c.year === 1954 && c.brand === "Bowman") || CARD_IDS.berra54,
          berra52t: find(c => c.last_name === "Berra"  && c.year === 1952 && c.brand === "Topps")  || CARD_IDS.berra52t,
          maris58:  find(c => c.last_name === "Maris"  && c.year === 1958)                         || CARD_IDS.maris58,
          mantle59: find(c => c.last_name === "Mantle" && c.year === 1959 && c.card_number === "10")  || find(c => c.last_name === "Mantle" && c.year === 1959) || CARD_IDS.mantle59,
          mantle61: find(c => c.last_name === "Mantle" && c.year === 1961 && c.card_number === "300") || find(c => c.last_name === "Mantle" && c.year === 1961) || CARD_IDS.mantle61,
          mantle58: find(c => c.last_name === "Mantle" && c.year === 1958 && c.card_number === "150") || find(c => c.last_name === "Mantle" && c.year === 1958) || CARD_IDS.mantle58,
        });
        const waxRes    = await api.get("/wax/");
        const binderRes = await api.get("/set-binders/");
        if (waxRes.data?.[0])    window._kioskWaxId    = waxRes.data[0].id;
        if (binderRes.data?.[0]) window._kioskBinderId = binderRes.data[0].id;
        // By the time API calls complete the URL is fully settled — read loop here
        const setupParams = new URLSearchParams(window.location.search);
        const setupLoopMap = { A:0, B:1, C:2, D:3, E:4, F:5 };
        const setupLoopKey = setupParams.get("loop");
        if (setupLoopKey && setupLoopMap[setupLoopKey] !== undefined) {
          setLoopIdx(setupLoopMap[setupLoopKey]);
        }
        setLoggedIn(true);
      } catch (e) {
        console.warn("Kiosk setup failed:", e.message);
      }
    })();
  }, [active, authLoggedIn]); // eslint-disable-line

  const clearTimers = useCallback(() => {
    clearTimeout(timerRef.current);
    runningRef.current = false;
  }, []);

  const jumpToLoop = (idx) => {
    clearTimers();
    setLoopIdx(idx);
    setPaused(false);
  };

  const runLoop = useCallback(async (loops, lIdx) => {
    if (!runningRef.current) return;
    const loop = loops[lIdx % loops.length];
    stepRef.current = 0;

    const runStep = async (idx) => {
      if (!runningRef.current || idx >= loop.length) {
        // Advance to next loop
        setLoopIdx(l => l + 1);
        timerRef.current = setTimeout(() => runLoop(loops, lIdx + 1), 2000);
        return;
      }

      const step = loop[idx];

      switch (step.type) {
        case "navigate":
          navigate(step.path);
          timerRef.current = setTimeout(() => runStep(idx + 1), 800);
          break;

        case "wait":
          timerRef.current = setTimeout(() => runStep(idx + 1), step.ms);
          break;

        case "caption":
          setCaption(step.text);
          timerRef.current = setTimeout(() => {
            setCaption("");
            setSpotlight(null);   // always clear spotlight when caption expires
            runStep(idx + 1);
          }, step.ms || 3000);
          break;

        case "scroll":
          window.scrollBy({ top: step.px, behavior: "smooth" });
          timerRef.current = setTimeout(() => runStep(idx + 1), step.ms || 80);
          break;

        case "table-scroll":
          window.dispatchEvent(new CustomEvent("kiosk-table-scroll", { detail: step.px }));
          timerRef.current = setTimeout(() => runStep(idx + 1), step.ms || 80);
          break;

        case "spotlight": {
          const el = document.querySelector(step.selector);
          if (el) {
            const r = el.getBoundingClientRect();
            setSpotlight({ top: r.top - 8, left: r.left - 8, width: r.width + 16, height: r.height + 16 });
          } else {
            setSpotlight(null);
          }
          timerRef.current = setTimeout(() => runStep(idx + 1), step.ms || 200);
          break;
        }

        case "spotlight-off":
          setSpotlight(null);
          timerRef.current = setTimeout(() => runStep(idx + 1), 200);
          break;

        case "open-sort-modal":
          window.dispatchEvent(new CustomEvent("kiosk-open-sort-modal"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 500);
          break;

        case "close-sort-modal":
          window.dispatchEvent(new CustomEvent("kiosk-close-sort-modal"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "edit-first-row":
          window.dispatchEvent(new CustomEvent("kiosk-edit-first-row"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 500);
          break;

        case "cancel-edit":
          window.dispatchEvent(new CustomEvent("kiosk-cancel-edit"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "show-new-row":
          window.dispatchEvent(new CustomEvent("kiosk-show-new-row"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 500);
          break;

        case "close-new-row":
          window.dispatchEvent(new CustomEvent("kiosk-close-new-row"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "smart-fill-demo": {
          // Open blank row, set year+brand context, then type first+last name char by char
          // With year+brand set, Smart Fill returns card_number and rookie for the specific card
          window.dispatchEvent(new CustomEvent("kiosk-open-blank-row"));
          setTimeout(() => {
            // Set year and brand first so Smart Fill has full context
            window.dispatchEvent(new CustomEvent("kiosk-type-new-row-field", { detail: { field: "year",  value: step.year  || "1968" } }));
            window.dispatchEvent(new CustomEvent("kiosk-type-new-row-field", { detail: { field: "brand", value: step.brand || "Topps" } }));

            const firstName = step.first || "Nolan";
            const lastName  = step.last  || "Ryan";
            let typed = "";
            let fidx = 0;

            const typeFirst = () => {
              if (!runningRef.current) return;
              if (fidx >= firstName.length) { typeLast(); return; }
              typed += firstName[fidx++];
              window.dispatchEvent(new CustomEvent("kiosk-type-new-row-field", { detail: { field: "first_name", value: typed } }));
              timerRef.current = setTimeout(typeFirst, 160);
            };

            let typedL = "";
            let lidx = 0;
            const typeLast = () => {
              if (!runningRef.current) return;
              if (lidx >= lastName.length) {
                timerRef.current = setTimeout(() => runStep(idx + 1), step.wait || 4000);
                return;
              }
              typedL += lastName[lidx++];
              window.dispatchEvent(new CustomEvent("kiosk-type-new-row-field", { detail: { field: "last_name", value: typedL } }));
              timerRef.current = setTimeout(typeLast, 160);
            };

            timerRef.current = setTimeout(typeFirst, 400);
          }, 600);
          break;
        }

        case "print-first-label":
          window.dispatchEvent(new CustomEvent("kiosk-print-first-label"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "admin-expand":
          window.dispatchEvent(new CustomEvent("kiosk-admin-expand", { detail: step.id }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "admin-collapse":
          window.dispatchEvent(new CustomEvent("kiosk-admin-collapse", { detail: step.id }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 300);
          break;

        case "admin-tab":
          window.dispatchEvent(new CustomEvent("kiosk-admin-tab", { detail: step.tab }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 500);
          break;

        case "focus-first-row":
          window.dispatchEvent(new CustomEvent("kiosk-focus-first-row"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 500);
          break;

        case "type-filter": {
          // Open the filter col, spotlight it, then type char by char
          const { col, field, value } = step;
          window.dispatchEvent(new CustomEvent("kiosk-open-filter", { detail: col }));
          setTimeout(() => {
            const selector = `[data-tour='col-${col}'] input`;
            const el = document.querySelector(selector);
            if (el) {
              const r = el.getBoundingClientRect();
              setSpotlight({ top: r.top - 8, left: r.left - 8, width: r.width + 16, height: r.height + 16 });
            }
            // Type one character at a time
            let typed = "";
            const chars = value.split("");
            const typeNext = (i) => {
              if (!runningRef.current) return;
              if (i >= chars.length) {
                timerRef.current = setTimeout(() => runStep(idx + 1), 400);
                return;
              }
              typed += chars[i];
              window.dispatchEvent(new CustomEvent("kiosk-filter", { detail: { field, value: typed } }));
              timerRef.current = setTimeout(() => typeNext(i + 1), 160);
            };
            typeNext(0);
          }, 500);
          break;
        }

        case "clear-filter": {
          const { col, field } = step;
          window.dispatchEvent(new CustomEvent("kiosk-filter", { detail: { field, value: "" } }));
          setSpotlight(null);
          // Close the filter col
          window.dispatchEvent(new CustomEvent("kiosk-open-filter", { detail: col + "_close" }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;
        }

        case "filter": {
          const el = document.querySelector(`[data-tour='col-last'] input, input[placeholder*="Filter"]`);
          if (step.field === "last_name") {
            window.dispatchEvent(new CustomEvent("kiosk-filter", { detail: { field: "last_name", value: step.value } }));
          } else if (step.field === "dict_search") {
            window.dispatchEvent(new CustomEvent("kiosk-filter", { detail: { field: "dict_search", value: step.value } }));
          }
          timerRef.current = setTimeout(() => runStep(idx + 1), 600);
          break;
        }

        case "sort":
          window.dispatchEvent(new CustomEvent("kiosk-sort", { detail: step.config }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 600);
          break;

        case "gallery-prev":
          window.dispatchEvent(new CustomEvent("kiosk-gallery-prev"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "gallery-zoom-in":
          window.dispatchEvent(new CustomEvent("kiosk-gallery-zoom-in"));
          timerRef.current = setTimeout(() => runStep(idx + 1), step.ms || 400);
          break;

        case "gallery-zoom-out":
          window.dispatchEvent(new CustomEvent("kiosk-gallery-zoom-out"));
          timerRef.current = setTimeout(() => runStep(idx + 1), step.ms || 400);
          break;

        case "gallery-next": {
          const count = step.count || 1;
          let done = 0;
          const tick = () => {
            if (done >= count || !runningRef.current) { runStep(idx + 1); return; }
            window.dispatchEvent(new CustomEvent("kiosk-gallery-next"));
            done++;
            timerRef.current = setTimeout(tick, step.interval || 1200);
          };
          tick();
          break;
        }

        case "gallery-flip":
          window.dispatchEvent(new CustomEvent("kiosk-gallery-flip"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "gallery-search":
          window.dispatchEvent(new CustomEvent("kiosk-gallery-search", { detail: step.value }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 600);
          break;

        case "analytics-click":
          window.dispatchEvent(new CustomEvent("kiosk-analytics-click", { detail: step }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "open-chat":
          window.dispatchEvent(new CustomEvent("kiosk-open-chat"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 800);
          break;

        case "close-chat":
          window.dispatchEvent(new CustomEvent("kiosk-close-chat"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 600);
          break;

        case "chat-qa":
          window.dispatchEvent(new CustomEvent("kiosk-chat-qa", { detail: CY_SCRIPT[step.index] }));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "print-label":
          window.dispatchEvent(new CustomEvent("kiosk-print-label"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        case "close-modal":
          window.dispatchEvent(new CustomEvent("kiosk-close-modal"));
          timerRef.current = setTimeout(() => runStep(idx + 1), 400);
          break;

        default:
          timerRef.current = setTimeout(() => runStep(idx + 1), 500);
      }
    };

    runStep(0);
  }, [navigate]);

  // Start running once logged in — use loopIdxRef so the start index is always current
  useEffect(() => {
    if (!loggedIn || paused) return;
    runningRef.current = true;
    const loops = buildLoops(cardIds);
    runLoop(loops, loopIdxRef.current);
    return () => clearTimers();
  }, [loggedIn, paused, cardIds]); // eslint-disable-line

  // Pause on click/tap
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      if (e.target.closest(".kiosk-pause-overlay")) return;
      clearTimers();
      setPaused(true);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [active, clearTimers]);

  if (!active) return null;

  return (
    <>
      <KioskCaption text={caption} />
      {spotlight && (
        <div style={{
          position: "fixed",
          top: spotlight.top, left: spotlight.left,
          width: spotlight.width, height: spotlight.height,
          borderRadius: 8,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
          zIndex: 7999, pointerEvents: "none",
          border: "2px solid #f59e0b",
          transition: "all 0.3s ease",
        }} />
      )}
      {paused && (
        <div className="kiosk-pause-overlay">
          <KioskPauseOverlay
            onResume={() => { setPaused(false); setLoggedIn(l => l); }}
            onJump={jumpToLoop}
          />
        </div>
      )}
    </>
  );
}
